// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { useEncryptAndPushBatch } from '../../hooks/useEncryptAndPush.js';
import { buildEvent } from '../../lib/eventLog.js';
import { COA_TEMPLATES, buildAccountPayloads, seedAccountNameKey } from '../../lib/coa-templates.js';
import type { CoaTemplate, CoaTemplateId, CoaTemplateNode } from '../../lib/coa-templates.js';
import { CURRENCIES, getCurrencyByCode } from '../../lib/currencies.js';
import type { LedgerEvent } from '../../types.js';

interface FirstRunWizardProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  /** Dismiss onboarding without seeding any accounts and enter the empty app. */
  onSkip: () => void;
}

type Step = 'template' | 'currency' | 'confirm';

/** Best-effort base-currency guess from the browser locale; falls back to USD. */
function guessBaseCurrency(): string {
  try {
    const resolved = new Intl.NumberFormat().resolvedOptions().currency;
    if (resolved && getCurrencyByCode(resolved)) return resolved.toUpperCase();
  } catch {
    /* Intl currency resolution is not guaranteed on every runtime */
  }
  return 'USD';
}

/** Count every account a template will create (roots + all descendants). */
function countNodes(nodes: CoaTemplateNode[]): number {
  return nodes.reduce((n, node) => n + 1 + (node.children ? countNodes(node.children) : 0), 0);
}

function StepDots({ step }: { step: Step }) {
  const order: Step[] = ['template', 'currency', 'confirm'];
  const active = order.indexOf(step);
  return (
    <div className="flex items-center gap-2 text-xs font-mono text-text-subtle">
      {order.map((s, i) => (
        <span key={s} className={i === active ? 'text-accent' : i < active ? 'text-text-muted' : ''}>
          {i + 1}
        </span>
      ))}
    </div>
  );
}

/** Recursive tree row for the confirmation preview. Indent scales with depth. */
function PreviewNode({ node, depth }: { node: CoaTemplateNode; depth: number }) {
  const { t } = useTranslation('onboarding');
  const isGroup = Boolean(node.children && node.children.length > 0);
  // Dynamic key (one per seed account); defaultValue falls back to English.
  const name = t(`seedAccounts.${seedAccountNameKey(node.name)}` as 'seedAccounts.checking', {
    defaultValue: node.name,
  });
  return (
    <>
      <div className="flex items-baseline justify-between py-0.5" style={{ paddingLeft: `${depth * 16}px` }}>
        <span className={isGroup ? 'text-sm text-text' : 'text-sm text-text-muted'}>{name}</span>
        <span className="text-xs font-mono text-text-subtle ml-3">{node.type}</span>
      </div>
      {node.children?.map((child) => (
        <PreviewNode key={child.name} node={child} depth={depth + 1} />
      ))}
    </>
  );
}

export function FirstRunWizard({ worker, keyData, onSkip }: FirstRunWizardProps) {
  const { t } = useTranslation(['onboarding', 'common']);
  const { state } = useApp();
  const navigate = useNavigate();
  const pushBatch = useEncryptAndPushBatch(worker, keyData);

  const [step, setStep] = useState<Step>('template');
  const [templateId, setTemplateId] = useState<CoaTemplateId | null>(null);
  const [baseCurrency, setBaseCurrency] = useState<string>(guessBaseCurrency);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');

  const template: CoaTemplate | null = templateId ? COA_TEMPLATES[templateId] : null;
  const accountCount = useMemo(() => (template ? countNodes(template.roots) : 0), [template]);

  async function handleCreate() {
    if (!template) return;
    setError('');
    setCommitting(true);
    try {
      const payloads = buildAccountPayloads(template, baseCurrency, (name) =>
        t(`seedAccounts.${seedAccountNameKey(name)}` as 'seedAccounts.checking', { defaultValue: name }),
      );
      const baseSeq = state.ledger.lastSequence + 1;

      // Seed the home currency first so the ledger's reporting currency matches
      // the accounts, then the accounts themselves — all appended as one blob.
      const events: LedgerEvent[] = await Promise.all([
        buildEvent('SettingUpdated', { key: 'homeCurrency', value: baseCurrency }, baseSeq),
        ...payloads.map((payload, i) => buildEvent('AccountCreated', payload, baseSeq + 1 + i)),
      ]);

      await pushBatch(events);
      navigate('/accounts', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('createError'));
      setCommitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg text-text flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-xl my-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl text-text">{t('title')}</h1>
            <p className="text-sm text-text-muted mt-1">
              {t('subtitle')}
            </p>
          </div>
          <StepDots step={step} />
        </div>

        {error && (
          <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
        )}

        {step === 'template' && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.values(COA_TEMPLATES).map((tpl) => {
                const selected = templateId === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setTemplateId(tpl.id)}
                    className={`text-left p-4 border transition-colors ${
                      selected
                        ? 'border-accent bg-bg-elevated'
                        : 'border-border bg-bg-surface hover:border-border-strong'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-md font-medium text-text">{t(`templates.${tpl.id}.label`)}</span>
                      <span className="text-xs font-mono text-text-subtle">
                        {t('accountCount', { count: countNodes(tpl.roots) })}
                      </span>
                    </div>
                    <p className="text-sm text-text-muted mt-2">{t(`templates.${tpl.id}.description`)}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end items-center gap-3 mt-6">
              <button
                type="button"
                disabled={!templateId}
                onClick={() => setStep('currency')}
                className="px-5 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
              >
                {t('common:actions.continue')}
              </button>
              <button
                type="button"
                onClick={onSkip}
                className="px-3 py-2 text-sm text-text-subtle hover:text-text-muted transition-colors"
              >
                {t('common:actions.skip')}
              </button>
            </div>
          </div>
        )}

        {step === 'currency' && (
          <div>
            <div className="bg-bg-surface border border-border p-4">
              <label className="block text-sm text-text-muted mb-2" htmlFor="base-currency">
                {t('baseCurrency')}
              </label>
              <select
                id="base-currency"
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value)}
                className="w-full bg-bg border border-border text-text text-sm font-mono px-3 py-2 focus:border-accent focus:outline-none"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-subtle mt-2">
                {t('baseCurrencyNote')}
              </p>
            </div>
            <div className="flex justify-between items-center mt-6">
              <button
                type="button"
                onClick={() => setStep('template')}
                className="px-5 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
              >
                {t('common:actions.back')}
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStep('confirm')}
                  className="px-5 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
                >
                  {t('common:actions.continue')}
                </button>
                <button
                  type="button"
                  onClick={onSkip}
                  className="px-3 py-2 text-sm text-text-subtle hover:text-text-muted transition-colors"
                >
                  {t('common:actions.skip')}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'confirm' && template && (
          <div>
            <div className="flex items-center justify-between mb-3 text-sm text-text-muted">
              <span>
                {t('templateWithCount', { label: t(`templates.${template.id}.label`), count: accountCount })}
              </span>
              <span className="font-mono text-text-subtle">{baseCurrency}</span>
            </div>
            <div className="bg-bg-surface border border-border max-h-[52vh] overflow-y-auto divide-y divide-border">
              {template.roots.map((root) => (
                <div key={root.name} className="px-4 py-2">
                  <PreviewNode node={root} depth={0} />
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-6">
              <button
                type="button"
                disabled={committing}
                onClick={() => setStep('currency')}
                className="px-5 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated disabled:opacity-40 transition-colors"
              >
                {t('common:actions.back')}
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={committing}
                  onClick={handleCreate}
                  className="px-5 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
                >
                  {committing ? t('creating') : t('createAccounts')}
                </button>
                <button
                  type="button"
                  disabled={committing}
                  onClick={onSkip}
                  className="px-3 py-2 text-sm text-text-subtle hover:text-text-muted disabled:opacity-40 transition-colors"
                >
                  {t('common:actions.skip')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
